import React from 'react';
import { withSitecoreContext, Text } from '@sitecore-jss/sitecore-jss-react';
import { compose } from 'ramda';

import { withEditMode, withEditModeNotice } from '_containers/BaseComponent';
import { Capacity as CapacitySVG, Size as SizeSVG, Tag as TagSVG } from '_utils/icons';
import { Grid } from '_utils/components/Corporate';

import DetailWithIcon from './DetailWithIcon';
import { ItemWrapper, Container, DetailLink, DetailText } from './StyledDetailWithIconList';
import { validTag } from '_components/Corporate/TagList';
import { buildSearchUrl } from '_utils/helpers';
import { BackgroundColors } from '_utils/components/Corporate/Section';
import { errorInDev } from '_utils/helpers/dev';
import { useTranslation } from 'react-i18next';

const Icons = {
  Capacity: CapacitySVG,
  Size: SizeSVG,
  Tag: TagSVG
};

type DetailListProps = {
  sitecoreContext: any;
  rendering: any;
  editMode: boolean;
  params: any;
};

enum Titles {
  Capacity = 'detail-with-icon-list-capacity-label',
  Size = 'detail-with-icon-list-size-label',
  UsedFor = 'detail-with-icon-list-used-for-label'
}

const getValidTags = (targetItems) => targetItems?.filter(validTag) || [];

const isValidField = (field) => {
  if (typeof field === 'undefined') {
    return false;
  }

  const { value = null } = field;

  if (value === null) {
    return false;
  }

  return typeof value === 'string' ? value.trim() : value.toString().trim();
};

const EditorText = compose(withEditModeNotice)(Text);

const DetailList: React.FC<DetailListProps> = ({
  sitecoreContext,
  rendering,
  editMode,
  params
}) => {
  let colSize: string | number = 'auto';
  const items = [];
  const [t] = useTranslation();

  // Default
  let backgroundColor = BackgroundColors.Grey;

  try {
    backgroundColor = BackgroundColors[params.backgroundColour];
  } catch {}

  try {
    const { capacity, capacityDescription } = sitecoreContext.route.fields;

    let textArea;

    if (editMode) {
      textArea = (
        <React.Fragment>
          <EditorText
            field={capacity}
            noticeEnabled
            noticeLevel="info"
            noticeMessage={`Capacity (numeric value only). 'Person/People' will be appened to the end of this automatically.`}
          />
          <EditorText
            field={capacityDescription}
            noticeEnabled
            noticeLevel="info"
            noticeMessage={`A '–' will be prepended to this field to seperate it from the capacity.`}
          />
        </React.Fragment>
      );
    } else {
      const isCapacityValid = isValidField(capacity);
      const isDescriptionValid = isValidField(capacityDescription);

      const capacityValue = isCapacityValid
      ? typeof capacity.value === 'number'
        ? capacity.value
        : parseInt(capacity.value)
      : null;

      // both not valid don't show for FE
      if (!isCapacityValid && !isDescriptionValid) {
        textArea = null;
      } else {
        textArea = (
          <React.Fragment>
            {isCapacityValid && (
              <React.Fragment>
                {capacityValue}{' '}
                {capacityValue === 1
                  ? t('space-capacity-unit-singular')
                  : t('space-capacity-unit-plural')}
              </React.Fragment>
            )}
            {isDescriptionValid && (
              <React.Fragment>
                {isCapacityValid && ' – '}
                <Text field={capacityDescription} />
              </React.Fragment>
            )}
          </React.Fragment>
        );
      }
    }

    if (textArea !== null) {
      items.push(
        <DetailWithIcon
          title={t(Titles.Capacity)}
          description={<DetailText>{textArea}</DetailText>}
          icon={Icons.Capacity}
        />
      );
    }
  } catch (e) {
    errorInDev(e);
  }

  try {
    const { size } = sitecoreContext.route.fields;

    // always for edit mode and if there is data for FE
    if (isValidField(size) || editMode) {
      items.push(
        <DetailWithIcon
          title={t(Titles.Size)}
          description={
            <DetailText>
              <Text field={size} />
            </DetailText>
          }
          icon={Icons.Size}
        />
      );
    }
  } catch (e) {
    errorInDev(e);
  }

  try {
    const renderingPageFields = rendering?.fields?.data?.pageFields || {};
    const usedFors = getValidTags(renderingPageFields?.usedFors?.targetItems);

    const tags = usedFors.map(
      ({
        displayName,
        id,
        friendlyName,
        parent: {
          identifier: { value: tagId }
        }
      }) => (
        <DetailLink
          key={id}
          to={buildSearchUrl({
            page: 1,
            'used-for': friendlyName
          })}
        >
          {displayName?.value}
        </DetailLink>
      )
    );

    if (tags.length > 0) {
      items.push(
        <DetailWithIcon
          title={t(Titles.UsedFor)}
          description={tags.map((l, i, a) => {
            if (i !== a.length - 1) {
              return <React.Fragment key={i}>{l}, </React.Fragment>;
            }

            return l;
          })}
          icon={Icons.Tag}
        />
      );
    }
  } catch (e) {
    errorInDev(e);
  }

  if (items.length === 1) {
    colSize = 8;
  }

  if (items.length === 0) {
    return null;
  }

  return (
    <Container className={backgroundColor} $sectionTheme={backgroundColor}>
      <Grid.Container>
        <Grid.Row
          $config={{
            gutters: ['left', 'right']
          }}
        >
          {items.map((item, i) => (
            <ItemWrapper
              key={i}
              $config={{
                col: {
                  xs: 12,
                  sm: colSize
                },
                gutters: ['left', 'right']
              }}
            >
              {item}
            </ItemWrapper>
          ))}
        </Grid.Row>
      </Grid.Container>
    </Container>
  );
};

export default compose(withSitecoreContext(), withEditMode, withEditModeNotice)(DetailList);
